import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FACES } from "~/lib/faces";
import { useMeterPct, useVisitor } from "~/lib/store";

const REVEAL_DELAY_MS = 800;
const ANIMATION_MS = 420;

/**
 * The end-of-exploration reveal. Triggers when the visitor has visited all
 * eight cubies (meter at 100%) and hasn't seen the reveal before. Persists
 * the "seen" flag to localStorage via the visitor store, so it fires at
 * most once per persistent visitor.
 *
 * Implements User Story 2's closing beat — the explicit moment the site
 * escalates from "show" to "ask." Visitor either taps through to /hire or
 * dismisses to keep poking around.
 */
export function TrustUnlocked() {
  const meterPct = useMeterPct();
  const seen = useVisitor((s) => s.trustUnlockedSeen);
  const markSeen = useVisitor((s) => s.markTrustUnlockedSeen);

  const [stage, setStage] = useState<"hidden" | "mounting" | "open">("hidden");

  useEffect(() => {
    if (stage !== "hidden") return;
    if (meterPct < 100 || seen) return;

    const showTimer = window.setTimeout(() => {
      setStage("mounting");
      requestAnimationFrame(() => setStage("open"));
    }, REVEAL_DELAY_MS);
    return () => window.clearTimeout(showTimer);
  }, [meterPct, seen, stage]);

  const dismiss = (): void => {
    setStage("hidden");
    markSeen();
  };

  useEffect(() => {
    if (stage === "hidden") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  if (stage === "hidden") return null;

  const opaque = stage === "open";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="trust-unlocked-title"
      className={[
        "fixed inset-0 z-[80] flex items-center justify-center px-4 py-8",
        "bg-jg-bg/85 backdrop-blur-md",
        "transition-opacity",
        opaque ? "opacity-100" : "opacity-0",
      ].join(" ")}
      style={{ transitionDuration: `${ANIMATION_MS}ms` }}
      onClick={dismiss}
    >
      <div
        className={[
          "relative w-full max-w-lg rounded-3xl border border-jg-border bg-jg-bg-soft/95 p-7 shadow-[0_30px_120px_-20px_rgba(199,255,92,0.35)] sm:p-10",
          "transition-all",
          opaque ? "scale-100 opacity-100" : "scale-95 opacity-0",
        ].join(" ")}
        style={{ transitionDuration: `${ANIMATION_MS}ms` }}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-jg-ai">
          Trust unlocked · 8 / 8
        </p>

        <h2
          id="trust-unlocked-title"
          className="mt-3 font-display text-2xl font-semibold leading-tight tracking-tight sm:text-3xl"
        >
          You've seen what{" "}
          <span className="text-jg-augment">JGson</span> builds.
        </h2>

        <p className="mt-3 text-sm leading-relaxed text-jg-fg-muted sm:text-base">
          Eight cubies. One engineer. Ten years of shipping across games,
          AR, mobile, React Native, AI, fintech, and the engines underneath.
          If any of it matches what you need, the brief form is two clicks
          away. Or just reply to this site with the address in the footer.
        </p>

        <p className="mt-2 font-mono text-xs text-jg-fg-muted">— João</p>

        <ul
          aria-label="Faces explored"
          className="mt-6 flex items-center justify-center gap-2.5"
        >
          {FACES.map((f) => (
            <li key={f.id}>
              <span
                className="block size-2.5 rounded-full"
                style={{
                  backgroundColor: f.themeColor,
                  boxShadow: `0 0 12px ${f.themeColor}80`,
                }}
                title={f.label}
              />
            </li>
          ))}
        </ul>

        <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={dismiss}
            className="rounded-full border border-jg-border bg-jg-bg-soft/60 px-4 py-2 text-sm font-medium text-jg-fg-muted transition-colors hover:text-jg-fg"
          >
            Keep exploring
          </button>
          <Link
            to="/hire"
            onClick={dismiss}
            className="rounded-full bg-jg-ai px-5 py-2 text-center text-sm font-semibold text-jg-bg transition-opacity hover:opacity-90"
          >
            Send a brief →
          </Link>
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full p-1 text-jg-fg-muted hover:text-jg-fg"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  );
}
